import React, { useEffect, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { TrendingUp, Globe2, Flame, MousePointerClick, FileText, Users, Wallet, Trophy } from 'lucide-react';
import { authFetch } from '../../lib/auth';

const ORANGE = '#F77F11';

function Kpi({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
      <Icon className="w-4 h-4 text-[#FF9B4A]" />
      <p className="mt-2 text-2xl font-bold tabular-nums text-white">{value}</p>
      <p className="mt-0.5 text-[10.5px] uppercase tracking-wider text-neutral-500 font-bold leading-tight">{label}</p>
      {sub && <p className="mt-0.5 text-[11px] text-neutral-500">{sub}</p>}
    </div>
  );
}

function Bar({ label, n, max }) {
  return (
    <div className="flex items-center gap-3">
      <p className="w-28 truncate text-[12px] text-neutral-300">{label}</p>
      <div className="flex-1 h-2.5 rounded-full bg-white/[0.06] overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-[#F77F11] to-[#FF9B4A]" style={{ width: `${Math.max((n / (max || 1)) * 100, 4)}%` }} />
      </div>
      <p className="w-8 text-right text-[12px] font-bold tabular-nums text-white">{n}</p>
    </div>
  );
}

const SEGMENTS = [
  { key: 'sellers', title: '🏆 Sellers', color: 'border-emerald-500/30 bg-emerald-500/[0.05]',
    tip: 'Already closing customers. Incentive: ask them for intros to other restaurant owners and feature them on the leaderboard.' },
  { key: 'in_play', title: '🎯 In play', color: 'border-sky-500/30 bg-sky-500/[0.05]',
    tip: 'Have referrals or proposals but no close yet. Incentive: help them follow up — offer to join a call with them.' },
  { key: 'sharing', title: '📣 Sharing', color: 'border-amber-500/30 bg-amber-500/[0.05]',
    tip: 'Generating clicks but no leads. Incentive: remind them of the proposal PDF generator and the Score™ as a door opener.' },
  { key: 'dormant', title: '😴 Dormant', color: 'border-red-500/30 bg-red-500/[0.05]',
    tip: 'No activity. Incentive: personal 1-on-1 message + simple challenge ("send your link to 3 contacts this week").' },
];

export const AdminPartnerInsights = () => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    authFetch('/affiliates/admin/insights').then(async (r) => {
      if (r.ok) setD(await r.json()); else setErr(`HTTP ${r.status}`);
    }).catch(() => setErr('network'));
  }, []);
  if (err) return null;
  if (!d) return <p className="mt-6 text-neutral-500 text-sm">Loading insights…</p>;

  const { kpis, series_30d, channels, ranking, geo, segments } = d;
  const delta = kpis.clicks_prev_30d > 0
    ? Math.round(((kpis.clicks_30d - kpis.clicks_prev_30d) / kpis.clicks_prev_30d) * 100)
    : null;
  const maxCh = Math.max(...channels.map((c) => c.n), 1);
  const maxCountry = Math.max(...geo.countries.map((c) => c.n), 1);
  const maxCity = Math.max(...geo.cities.map((c) => c.n), 1);
  const medal = (i) => (i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`);
  const timeAgo = (iso) => {
    if (!iso) return 'never';
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    return days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days}d ago`;
  };

  return (
    <div className="mt-8" data-testid="admin-partner-insights">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-insights-kpis">
        <Kpi icon={Users} label="Partners" value={kpis.partners.approved} sub={`${kpis.partners.pending} pending`} />
        <Kpi icon={MousePointerClick} label="Clicks 30d" value={kpis.clicks_30d} sub={delta !== null ? `${delta >= 0 ? '+' : ''}${delta}% vs prev 30d` : '—'} />
        <Kpi icon={Flame} label="Partner scans 30d" value={kpis.scans_30d} />
        <Kpi icon={FileText} label="Proposals 30d" value={kpis.proposals_30d} />
        <Kpi icon={Wallet} label="Commission pending" value={`$${kpis.commissions_pending}`} />
        <Kpi icon={Wallet} label="Commission paid" value={`$${kpis.commissions_paid}`} />
      </div>

      {/* Chart + channels */}
      <div className="mt-4 grid lg:grid-cols-[1.6fr,1fr] gap-4">
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5" data-testid="admin-insights-chart">
          <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#FF9B4A] font-bold">
            <TrendingUp className="w-4 h-4" /> Channel activity — 30 days (clicks & scans)
          </p>
          <div className="mt-3 h-52">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series_30d} margin={{ top: 4, right: 4, left: -26, bottom: 0 }}>
                <defs>
                  <linearGradient id="gClicks" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={ORANGE} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={ORANGE} stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="gScans" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#38bdf8" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="d" tick={{ fill: '#6b7280', fontSize: 10 }} tickLine={false} axisLine={false} interval={4} />
                <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#1B1F24', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, fontSize: 12 }}
                  labelStyle={{ color: '#9ca3af' }} />
                <Area type="monotone" dataKey="clicks" name="Clicks" stroke={ORANGE} strokeWidth={2} fill="url(#gClicks)" />
                <Area type="monotone" dataKey="scans" name="Scans" stroke="#38bdf8" strokeWidth={2} fill="url(#gScans)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5" data-testid="admin-insights-channels">
          <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-bold">Clicks by channel</p>
          <div className="mt-4 space-y-2.5">
            {channels.length === 0 ? <p className="text-[12px] text-neutral-600">No clicks yet.</p> :
              channels.map((c) => <Bar key={c.ch} label={c.ch} n={c.n} max={maxCh} />)}
          </div>
        </div>
      </div>

      {/* Segments — who needs what */}
      <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-insights-segments">
        {SEGMENTS.map((s) => (
          <div key={s.key} className={`rounded-2xl border p-4 ${s.color}`}>
            <p className="text-[13px] font-bold text-white">{s.title} <span className="ml-1 text-neutral-400 font-normal tabular-nums">({segments[s.key].length})</span></p>
            <p className="mt-1 text-[11px] text-neutral-400 leading-relaxed">{s.tip}</p>
            {segments[s.key].length > 0 && (
              <p className="mt-2 text-[11.5px] text-neutral-300 truncate">{segments[s.key].slice(0, 4).join(', ')}{segments[s.key].length > 4 ? '…' : ''}</p>
            )}
          </div>
        ))}
      </div>

      {/* Ranking + Geo */}
      <div className="mt-4 grid lg:grid-cols-[1.6fr,1fr] gap-4">
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5" data-testid="admin-insights-ranking">
          <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#FF9B4A] font-bold">
            <Trophy className="w-4 h-4" /> Partner ranking — performance & access
          </p>
          <div className="mt-3 space-y-1.5">
            {ranking.length === 0 ? <p className="text-[12px] text-neutral-600">No approved partners yet.</p> :
              ranking.slice(0, 10).map((r, i) => (
                <div key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl bg-white/[0.03] px-3.5 py-2.5" data-testid={`admin-rank-row-${i}`}>
                  <span className="w-6 text-[13px]">{medal(i)}</span>
                  <div className="flex-1 min-w-[150px]">
                    <p className="text-[12.5px] font-semibold text-white truncate">{r.name} <span className="text-[10.5px] font-bold text-[#FF9B4A]">{r.tier.tier_name}</span></p>
                    <p className="text-[10.5px] text-neutral-500 truncate">{[r.city, r.country].filter(Boolean).join(', ') || '—'}{r.audience ? ` · ${r.audience.slice(0, 44)}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-neutral-400 tabular-nums">
                    <span title="clicks (30d)">{r.clicks} <span className="text-neutral-600">({r.clicks_30d})</span> clicks</span>
                    <span>{r.scans} scans</span>
                    <span>{r.proposals} PDFs</span>
                    <span>{r.referrals} refs</span>
                    <span className="text-white font-bold">{r.customers} 👥</span>
                    <span className="text-neutral-500" title={`${r.login_count} logins`}>last access: {timeAgo(r.last_login_at)}</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5" data-testid="admin-insights-geo">
          <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#FF9B4A] font-bold">
            <Globe2 className="w-4 h-4" /> Channel map — countries & cities
          </p>
          {geo.countries.length === 0 && geo.cities.length === 0 ? (
            <p className="mt-3 text-[12px] text-neutral-600">No geo data yet — it is captured automatically on every signup.</p>
          ) : (
            <>
              {geo.countries.length > 0 && (
                <div className="mt-4 space-y-2.5">
                  {geo.countries.map((c) => <Bar key={c.name} label={c.name} n={c.n} max={maxCountry} />)}
                </div>
              )}
              {geo.cities.length > 0 && (
                <>
                  <p className="mt-5 text-[10.5px] uppercase tracking-wider text-neutral-500 font-bold">Cities</p>
                  <div className="mt-2 space-y-2">
                    {geo.cities.map((c) => <Bar key={c.name} label={c.name} n={c.n} max={maxCity} />)}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
